import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Helmet } from 'react-helmet';

import axios from '@core/frontend/http';
import { withErrorHandler } from '@core/frontend/hoc';
import { BlockRendererConfigProvider, ConsentModal, Footer, Header } from '@core/frontend/components';
import { AppRoutes } from '@core/frontend/routes';
import { ScrollToTop, personalization, productFeatures, statisticTracking, useIsPreview } from '@core/frontend/shared';
import {
    FocusModePriority,
    useAchievementsSummary,
    useComputedFocusMode,
    useConfigStore,
    useDesignLivePreview,
    useFooterConfig,
    useLivePreviewStore,
    useMetricsConfig,
    useRegisterFocusMode,
} from '@core/frontend/stores';
import { mergeClassNames } from '@core/shared';
import { AchievementsSummaryContext } from '@core/frontend/ui';

import { ProductProvider } from './shared/productContext.jsx';

/**
 * App-Shell beider Frontends.
 *
 * Alles, was sich zwischen den Produkten unterscheidet, kommt als Deskriptor herein
 * (`apps/<app>/src/product.js`): Zähler, Hero-Policy, Jury-Texte, Container, Zusatzrouten,
 * Consent-Präfix, Block-Renderer und das Preview-Gate. Der Deskriptor wird zugleich als
 * `ProductProvider` gesetzt, damit Container ohne Prop-Durchreichung daran kommen.
 */
function App({ product }) {
    const location = useLocation();
    useDesignLivePreview();
    const achievementsSummary = useAchievementsSummary();
    const configureFeatures = useConfigStore((state) => state.configureFeatures);
    const isJuryRoot = location.pathname.startsWith('/jury');
    const { gap: { desktop: metricsGapValueDesktop, mobile: metricsGapValueMobile } = {} } = useMetricsConfig();
    const containerClasses = mergeClassNames(
        'container-lg',
        'main-content-container',
        metricsGapValueDesktop !== undefined ? 'desktop-gap-configured' : '',
        metricsGapValueMobile !== undefined ? 'mobile-gap-configured' : ''
    );
    const [pageNotFound, setPageNotFound] = useState(false);
    const [pages, setPages] = useState(null);
    const [navigation, setNavigation] = useState({});
    const [previewPageData, setPreviewPageData] = useState(null);

    // Die Fähigkeiten des Backends stehen nicht in `/config`; sie kommen aus
    // `config/app.config.json` (`products.<id>`, per `define` durchgereicht) und werden
    // freigeschaltet, bevor die Konfiguration geholt wird (Vorgabe: alles aus).
    useEffect(() => {
        void configureFeatures(productFeatures);
    }, [configureFeatures]);

    const pageNotFoundCallback = (pageState) => {
        setPageNotFound(pageState);
    };

    useEffect(() => {
        const pages = axios.get('/pages/');
        const navigation = axios.get('/menus/');

        Promise.all([pages, navigation])
            .then(([resPages, resNavigation]) => {
                if (process.env.REACT_APP_BUILD_FOR_ARCHIVAL === 'true') {
                    resNavigation.data = resNavigation.data.filter((item) => item.linkTarget !== '/bewerbung');
                }

                const primaryNavigation = resNavigation.data.filter((menuItem) => {
                    return menuItem.position === 'primary' && menuItem.highlight === false;
                });
                const primaryNavigationCTA = resNavigation.data.filter((menuItem) => {
                    return menuItem.position === 'primary' && menuItem.highlight === true;
                });
                const secondaryNavigation = resNavigation.data.filter((menuItem) => {
                    return menuItem.position === 'secondary';
                });
                setPages(resPages.data);
                setNavigation({
                    primaryNavigation: primaryNavigation,
                    primaryNavigationCTA: primaryNavigationCTA,
                    secondaryNavigation: secondaryNavigation,
                });
            })
            .catch((error) => {
                console.log(error);
            });
    }, []);

    let dynamicHeader = null;
    let consentManagementModal = null;
    const isPreview = useIsPreview();
    const isPreviewConnected = useLivePreviewStore((state) => state.isPreviewConnected);

    // Check if cookies exist and display consent management modal OR load tracking scripts.
    // The tracking scripts cannot be retracted once injected, so their suppression is
    // decided synchronously by the product's preview gate (dedicated preview route or
    // unambiguous dashboard embedding). The consent modal additionally disappears as soon as
    // a live preview connection is confirmed via the postMessage handshake (livePreviewStore).
    if (!product.previewGate({ location, isPreview, isPreviewConnected })) {
        const consentPrefix = product.consentCookiePrefix;
        if (
            Cookies.get(`${consentPrefix}_personalization-consent`) === 'true' &&
            Cookies.get(`${consentPrefix}_statistic-consent`) === 'true'
        ) {
            dynamicHeader = (
                <Helmet>
                    {personalization()}
                    {statisticTracking()}
                </Helmet>
            );
        } else if (Cookies.get(`${consentPrefix}_statistic-consent`) === 'true') {
            dynamicHeader = <Helmet>{statisticTracking()}</Helmet>;
        } else if (Cookies.get(`${consentPrefix}_personalization-consent`) === 'true') {
            dynamicHeader = <Helmet>{personalization()}</Helmet>;
        }

        if (Cookies.get(`${consentPrefix}_cookie-consent`) !== 'true') {
            consentManagementModal = <ConsentModal pages={pages} cookiePrefix={product.consentCookiePrefix} />;
        }
    }

    const { isFullWidth: isFooterFullWidth } = useFooterConfig();

    const currentSlug = location.pathname.startsWith('/') ? location.pathname.substring(1) : location.pathname;
    const currentPage = pages?.find((page) => page.slug === currentSlug);

    const globalFocusDesktop = isPreview
        ? Boolean(previewPageData?.isFocusModeDesktop)
        : Boolean(currentPage?.isFocusModeDesktop);
    const globalFocusMobile = isPreview
        ? Boolean(previewPageData?.isFocusModeMobile)
        : Boolean(currentPage?.isFocusModeMobile);

    useRegisterFocusMode('global', FocusModePriority.PAGE, globalFocusDesktop, globalFocusMobile);

    const { isFocusModeDesktop, isFocusModeMobile } = useComputedFocusMode();

    // Die Errungenschaften sind geteilte Gamification; der Provider deckt alle Verbraucher im
    // Routenbaum ab (`AchievementsOverview`, `Badge`). Ein produktseigener `renderGate` bleibt
    // darin verschachtelt, damit Produkte weitere Provider ergänzen können.
    const productGate = product.renderGate ?? ((routeTree) => routeTree);

    return (
        <ProductProvider value={product}>
            <BlockRendererConfigProvider value={product.blockRendererConfig}>
                <div className="w-100 overflow-x-hidden">
                    {dynamicHeader}
                    {consentManagementModal}
                    {!isJuryRoot && (
                        <Header
                            primaryNavigation={navigation.primaryNavigation}
                            primaryNavigationCTA={navigation.primaryNavigationCTA}
                            pageNotFound={pageNotFound}
                            isFocusModeDesktop={isFocusModeDesktop}
                            isFocusModeMobile={isFocusModeMobile}
                            counter={product.counter}
                            resolveHeroVariant={product.resolveHeroVariant}
                            resolveMetricsPlacement={product.resolveMetricsPlacement}
                        />
                    )}{' '}
                    <ScrollToTop />
                    <div className="visually-hidden" id="content" />
                    <main className={isJuryRoot ? '' : containerClasses}>
                        <AppRoutes
                            pages={pages}
                            containers={product.containers}
                            extraRoutes={product.extraRoutes}
                            juryCopy={product.juryCopy}
                            juryVotingModel={product.votingModel}
                            renderGate={(routeTree) => (
                                <AchievementsSummaryContext.Provider value={achievementsSummary || {}}>
                                    {productGate(routeTree)}
                                </AchievementsSummaryContext.Provider>
                            )}
                            pageNotFoundCallback={pageNotFoundCallback}
                            onPageDataChange={setPreviewPageData}
                        />
                    </main>
                    <Footer
                        secondaryNavigation={navigation.secondaryNavigation}
                        consentCookieName={`${product.consentCookiePrefix}_cookie-consent`}
                        contained={isJuryRoot || !isFooterFullWidth}
                        isFocusModeDesktop={isFocusModeDesktop}
                        isFocusModeMobile={isFocusModeMobile}
                    />
                </div>
            </BlockRendererConfigProvider>
        </ProductProvider>
    );
}

export default withErrorHandler(App, axios);
